// ABOUTME: Column-count changes that preserve the user's arrangement.
// ABOUTME: Replaces a row-major redistribution that scrambled layouts irreversibly.

import type { DashboardItem } from "../services/dashboardService";

export const MIN_COLUMNS = 2;
export const MAX_COLUMNS = 5;

/**
 * Recomputes item placement for a new column count.
 *
 * The previous implementation flattened everything by (col, order) and dealt it
 * back out row-major — `col = idx % count; order = floor(idx / count)`. That
 * interleaved items across columns on every change and was not reversible:
 * going 3 -> 4 -> 3 did not restore the original arrangement, with no undo.
 *
 * This preserves column identity instead:
 *  - Growing: nothing moves. The new columns simply start empty.
 *  - Shrinking: only items in the columns being removed move, appended in
 *    order to the end of the last surviving column. Everything else stays put.
 *
 * That makes grow/shrink round-trips a no-op whenever the removed columns were
 * empty, which is the common case.
 */
export function reflowForColumnCount(items: DashboardItem[], nextCount: number): DashboardItem[] {
  const count = Math.max(1, Math.floor(nextCount));

  const inRange = (item: DashboardItem) =>
    typeof item.col === "number" && item.col >= 0 && item.col < count;

  // Anything already inside the new range keeps its exact position.
  const kept = items.filter(inRange);

  // Orphans: columns being removed, plus any pre-existing out-of-range strays.
  const orphans = items
    .filter((item) => !inRange(item))
    .sort((a, b) => {
      const colA = typeof a.col === "number" ? a.col : 0;
      const colB = typeof b.col === "number" ? b.col : 0;
      if (colA !== colB) return colA - colB;
      return (a.order ?? 0) - (b.order ?? 0);
    });

  if (orphans.length === 0) return items;

  const target = count - 1;
  const targetColumn = kept
    .filter((i) => i.col === target)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  let nextOrder = targetColumn.length ? Math.max(...targetColumn.map((i) => i.order ?? 0)) + 1 : 0;

  const relocated = orphans.map((item) => ({
    ...item,
    col: target,
    order: nextOrder++,
  }));

  return [...kept, ...relocated];
}

/** Clamps a requested column count to the supported range. */
export function clampColumnCount(count: number): number {
  if (!Number.isFinite(count)) return MIN_COLUMNS;
  return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, Math.floor(count)));
}
